import { useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { t } from "@/i18n-js/instance";
import { BlockDmsModal } from "@/react/components/Chat/sharedV2/BlockDmsModal";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import { useBlockDms } from "@/react/hooks/chatsV2/blockDms/useBlockDms";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";
import { Typography } from "@circle-react/components/shared/uikit/Typography";
import { useChatParticipantsApi } from "@circle-react/hooks/chatsV2";
import { Loader } from "@circle-react-shared/uikit/Loader";
import { useToast } from "@circle-react-uikit/ToastV2";
import { ParticipantList } from "./ParticipantList";
import { mapParticipantsPerStatus, sortParticipants } from "./helpers";
import { useParticipantActions } from "./useParticipantActions";

export interface MemberListProps {
  chatRoom: any;
  refetchChatRoom: () => void;
}

export const MemberList = ({ chatRoom, refetchChatRoom }: MemberListProps) => {
  const memberProfileDrawer = useProfileDrawer();
  const [isBlockDmsModalOpen, setIsBlockDmsModalOpen] = useState(false);
  const [memberToBlock, setMemberToBlock] = useState<any>(null);
  const { uuid, current_participant: currentParticipant } = chatRoom;
  const { canBlockMemberDm } = useBlockDms();
  const toastr = useToast();

  const {
    chatParticipants = [],
    refetchChatParticipants,
    totalCount: totalParticipantsCount,
    hasNextPage,
    fetchNextPage,
  } = useChatParticipantsApi(uuid);

  const participants = [...sortParticipants(chatParticipants)];
  const { handleMakeAdmin, removeParticipant } = useParticipantActions({
    refetchChatParticipants,
    refetchChatRoom,
    uuid,
  });

  const showMemberProfile = (memberPublicId: string) => {
    if (memberPublicId) {
      void memberProfileDrawer.show({
        memberPublicId,
      });
    }
  };

  const toggleBlockDmsModal = (participant: ChatRoomParticipant) => {
    setMemberToBlock(participant);
    setIsBlockDmsModalOpen(true);
  };

  const handleBlockDms = () => {
    setIsBlockDmsModalOpen(false);
    toastr.success(
      t("messaging.block_direct_messages_modal.success", {
        name: memberToBlock?.name,
      }),
    );
  };

  const getProfileActionItems = (participant: ChatRoomParticipant) => {
    const profileActionItems: any[] = [
      {
        value: "view-profile",
        icon: "20-eye-open",
        iconSize: 20,
        className: "!py-2.5 !px-3",
        label: t("view_profile"),
        onClick: () => showMemberProfile(participant.user_public_uid || ""),
      },
      {
        value: participant.admin ? "remove-admin" : "make-admin",
        label: participant.admin
          ? t("messaging.remove_admin")
          : t("messaging.make_admin"),
        icon: "20-moderator",
        iconSize: 20,
        className: "!py-2.5 !px-3",
        onClick: () =>
          handleMakeAdmin({
            participantId: participant.id,
            status: !participant.admin,
          }),
      },
    ];

    if (
      canBlockMemberDm({
        admin: participant.community_admin,
        community_member_id: participant.community_member_id,
      })
    ) {
      profileActionItems.push({
        value: "block-dm",
        icon: "20-block-dms",
        className: "!py-2.5 !px-3",
        iconSize: 20,
        label: t("messaging.block_direct_messages"),
        iconClassName: "text-feedback-error",
        color: "text-feedback-error",
        onClick: () => toggleBlockDmsModal(participant),
      });
    }

    profileActionItems.push({
      value: "remove",
      label: t("messaging.remove"),
      icon: "20-close",
      iconSize: 20,
      className: "!py-2.5 !px-3",
      iconClassName: "text-feedback-error",
      color: "text-feedback-error",
      onClick: () => removeParticipant({ participantId: participant.id }),
    });
    return profileActionItems;
  };

  const { onlineParticipants, offlineParticipants } =
    mapParticipantsPerStatus(participants);

  return (
    <>
      <div
        id="scrollableDiv"
        className="messaging-rail__content h-full overflow-auto"
      >
        <InfiniteScroll
          scrollThreshold={0.8}
          next={fetchNextPage}
          hasMore={Boolean(hasNextPage)}
          dataLength={totalParticipantsCount}
          className="px-6 pb-6"
          scrollableTarget="scrollableDiv"
          loader={<Loader center />}
        >
          {onlineParticipants.length > 0 && (
            <>
              <div className="text-default mb-2">
                <Typography.LabelXxs weight="bold" color="text-default">
                  {t("chat_space.online")}
                </Typography.LabelXxs>
              </div>
              <ParticipantList
                currentParticipant={currentParticipant}
                getProfileActionItems={getProfileActionItems}
                participantList={onlineParticipants}
                showMemberProfile={showMemberProfile}
              />
            </>
          )}
          {offlineParticipants.length > 0 && (
            <>
              <div className="text-default my-2">
                <Typography.LabelXxs weight="bold" color="text-default">
                  {t("chat_space.offline")}
                </Typography.LabelXxs>
              </div>
              <ParticipantList
                currentParticipant={currentParticipant}
                getProfileActionItems={getProfileActionItems}
                participantList={offlineParticipants}
                showMemberProfile={showMemberProfile}
              />
            </>
          )}
        </InfiniteScroll>
      </div>
      <BlockDmsModal
        showModal={isBlockDmsModalOpen}
        onClose={() => setIsBlockDmsModalOpen(false)}
        onConfirm={handleBlockDms}
        memberName={memberToBlock?.name}
      />
    </>
  );
};
